import type { ViewController } from '@presentation/shell/NexxusShell';
import { ToastService } from '@presentation/components/Toast';
import { loadRegister, saveRegister } from '@presentation/shell/localCrud';
import { uuid } from '@shared/utils/uuid';
import { esc } from '@presentation/shell/format';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { downloadCsv } from '@presentation/shell/download';
import {
  PRIVACY_DATA_CATEGORIES,
  activePrivacyPolicy,
  privacyPolicySummary,
  type PrivacyPolicy,
  type PrivacyRetentionRule,
} from '@domain/legal/PrivacyPolicy';
import {
  CONSENT_METHODS,
  type DataConsent,
} from '@domain/legal/DataConsent';
import { openFormModal } from '@presentation/shell/openFormModal';
import { FormHelper } from '@presentation/components/FormHelper';

function tenantIdOf(ctx: Parameters<ViewController>[1]): string {
  return ctx.tenantContext.tryGetTenant()?.id ?? 'default';
}

export const confidentialiteController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canWrite = canMutateRecords(shell.permission);
  const canMutate = canWrite;
  let policies = await loadRegister<PrivacyPolicy>(tid, 'legal.privacy_policies');
  const session = ctx.auth.getSession();
  const { policies: ensured, seeded } = (
    await import('@domain/legal/DefaultPrivacyPolicy')
  ).ensureActivePrivacyPolicy(policies, session?.user_id ?? 'system');
  if (seeded) {
    policies = ensured;
    await saveRegister(tid, 'legal.privacy_policies', policies);
    ToastService.success('Politique de confidentialité type publiée automatiquement');
  }
  let consents = await loadRegister<DataConsent>(tid, 'legal.data_consents');
  const salaries = await ctx.salaries.list();

  const renderPolicies = (): void => {
    const banner = section.querySelector<HTMLElement>('#privacy-empty-banner');
    const tbody = section.querySelector('#privacy-versions-tbody');
    const active = activePrivacyPolicy(policies);
    if (banner) banner.style.display = active ? 'none' : '';
    if (!tbody) return;
    tbody.innerHTML =
      policies.length === 0
        ? ''
        : [...policies]
            .sort((a, b) => b.created_at.localeCompare(a.created_at))
            .map(
              (p) => `<tr>
          <td class="mono">${esc(p.version)}</td>
          <td>${esc(p.effective_date)}</td>
          <td><span class="pill ${p.is_active ? 'success' : ''}">${p.is_active ? 'ACTIVE' : 'ARCHIVÉE'}</span></td>
          <td>${esc(p.dpo_name || '—')}</td>
          <td class="num">${canWrite ? `<button type="button" class="btn btn-ghost btn-sm" data-view-policy="${esc(p.id)}">Voir</button>` : '—'}</td>
        </tr>`,
            )
            .join('');
  };

  const renderConsents = (): void => {
    const tbody = section.querySelector('#privacy-consents-tbody');
    if (!tbody) return;
    const nameById = new Map(salaries.map((s) => [s.id, `${s.prenom} ${s.nom}`]));
    tbody.innerHTML =
      consents.length === 0
        ? '<tr><td colspan="7" style="color:var(--gris)">Aucun consentement enregistré.</td></tr>'
        : [...consents]
            .sort((a, b) => b.consent_date.localeCompare(a.consent_date))
            .map((c) => {
              const name =
                c.subject_type === 'employee'
                  ? nameById.get(c.subject_id) ?? c.subject_id
                  : c.subject_id;
              const method =
                CONSENT_METHODS.find((m) => m.value === c.consent_method)?.label ??
                c.consent_method;
              const statut = c.withdrawn_at
                ? 'RETIRÉ'
                : c.consent_given
                  ? 'CONSENTI'
                  : 'REFUSÉ';
              return `<tr>
          <td>${esc(name)}</td>
          <td>${c.subject_type === 'employee' ? 'Salarié' : 'Candidat'}</td>
          <td>${esc(c.consent_date.slice(0, 10))}</td>
          <td>${esc(method)}</td>
          <td class="mono">${esc(c.policy_version)}</td>
          <td><span class="pill ${c.withdrawn_at ? 'warning' : c.consent_given ? 'success' : ''}">${statut}</span></td>
          <td class="num">${
            canWrite && !c.withdrawn_at && c.subject_type === 'employee'
              ? `<button type="button" class="btn btn-ghost btn-sm" data-withdraw="${esc(c.id)}">Retrait</button>`
              : '—'
          }</td>
        </tr>`;
            })
            .join('');
  };

  const render = (): void => {
    renderPolicies();
    renderConsents();
    const summary = section.querySelector('#privacy-active-summary');
    if (summary) summary.textContent = privacyPolicySummary(activePrivacyPolicy(policies));
  };
  render();

  section.querySelector('#btn-new-privacy-version')?.addEventListener('click', () => {
    if (!canWrite) {
      ToastService.warning('Publication réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    openFormModal(section, { title: 'Nouvelle politique de confidentialité', size: 'lg' }, (host, close) => {
      const catChecks = PRIVACY_DATA_CATEGORIES.map(
        (c) =>
          `<label style="display:flex;gap:8px;align-items:center;font-size:13px"><input type="checkbox" name="cat" value="${c.code}" checked> ${c.label}</label>`,
      ).join('');
      host.innerHTML = `
        <form id="privacy-form">
          <div class="form-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
            ${FormHelper.field('version', 'Version', 'text', { required: true, placeholder: 'v1.0', value: `v${policies.length + 1}.0` })}
            ${FormHelper.field('effective_date', 'Date d’entrée en vigueur', 'date', { required: true, value: new Date().toISOString().slice(0, 10) })}
            ${FormHelper.field('dpo_name', 'Nom du DPO', 'text', { required: false })}
            ${FormHelper.field('dpo_email', 'Email DPO', 'email', { required: false })}
            ${FormHelper.field('dpo_phone', 'Téléphone DPO', 'text', { required: false })}
          </div>
          <div class="field" style="margin-top:10px">
            <label>Corps de la politique *</label>
            <textarea name="content" rows="8" required style="width:100%;font:inherit;padding:8px;border:1px solid #e1e4e8;border-radius:4px" placeholder="Décrivez les finalités, bases légales, droits des personnes…"></textarea>
          </div>
          <div style="margin-top:12px"><strong style="font-size:13px">Catégories de données traitées</strong>
            <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:6px;margin-top:8px">${catChecks}</div>
          </div>
          <div class="field" style="margin-top:12px">
            <label>Durées de conservation (une ligne : catégorie | durée | base légale)</label>
            <textarea name="retention" rows="4" style="width:100%;font:inherit;padding:8px;border:1px solid #e1e4e8;border-radius:4px" placeholder="salarial | 5 ans | Code du travail Gabon&#10;identite | durée du contrat | Loi 001/2011"></textarea>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px">
            <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
            <button type="submit" class="btn btn-primary">Publier</button>
          </div>
        </form>`;
      host.querySelector('[data-cancel]')?.addEventListener('click', close);
      const form = host.querySelector<HTMLFormElement>('#privacy-form');
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        void (async () => {
          const fd = new FormData(form);
          const version = String(fd.get('version') ?? '').trim();
          const effective_date = String(fd.get('effective_date') ?? '').trim();
          const content = String(fd.get('content') ?? '').trim();
          if (!version || !effective_date || !content) {
            ToastService.error('Version, date et contenu sont obligatoires');
            return;
          }
          const data_categories = Array.from(form.querySelectorAll<HTMLInputElement>('input[name=cat]:checked')).map(
            (el) => el.value,
          );
          const retention_rules: PrivacyRetentionRule[] = String(fd.get('retention') ?? '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => {
              const [category, duration_label, legal_basis] = line.split('|').map((s) => s.trim());
              return {
                category: category || 'autre',
                duration_label: duration_label || '—',
                legal_basis: legal_basis || '—',
              };
            });
          const session = ctx.auth.getSession();
          const now = new Date().toISOString();
          const row: PrivacyPolicy = {
            id: uuid('priv'),
            version,
            effective_date,
            content,
            data_categories,
            retention_rules,
            dpo_name: String(fd.get('dpo_name') ?? '').trim() || undefined,
            dpo_email: String(fd.get('dpo_email') ?? '').trim() || undefined,
            dpo_phone: String(fd.get('dpo_phone') ?? '').trim() || undefined,
            is_active: true,
            created_by: session?.user_id ?? 'admin',
            created_at: now,
          };
          policies = policies.map((p) => ({ ...p, is_active: false }));
          policies = [...policies, row];
          await saveRegister(tid, 'legal.privacy_policies', policies);
          await recordAuditEvent(ctx, {
            action: 'create',
            entity: 'privacy_policy',
            entity_id: row.id,
            description: `Politique de confidentialité ${row.version} publiée`,
          });
          ToastService.success(`Politique ${row.version} publiée`);
          close();
          render();
        })();
      });
    });
  });

  section.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-view-policy]');
    if (btn?.dataset.viewPolicy) {
      const p = policies.find((x) => x.id === btn.dataset.viewPolicy);
      if (!p) return;
      openFormModal(section, { title: `Politique ${p.version}`, size: 'lg' }, (host, close) => {
        host.innerHTML = `<div style="white-space:pre-wrap;font-size:13px;line-height:1.55">${esc(p.content)}</div>
          <div style="margin-top:16px;text-align:right"><button type="button" class="btn btn-primary" data-close>Fermer</button></div>`;
        host.querySelector('[data-close]')?.addEventListener('click', close);
      });
      return;
    }
    const withdraw = (e.target as HTMLElement).closest<HTMLElement>('[data-withdraw]');
    if (withdraw?.dataset.withdraw && canWrite) {
      void (async () => {
        const id = withdraw.dataset.withdraw!;
        const session = ctx.auth.getSession();
        consents = consents.map((c) =>
          c.id === id
            ? {
                ...c,
                withdrawn_at: new Date().toISOString(),
                withdrawn_by: session?.user_id ?? 'admin',
              }
            : c,
        );
        await saveRegister(tid, 'legal.data_consents', consents);
        await recordAuditEvent(ctx, {
          action: 'update',
          entity: 'data_consent',
          entity_id: id,
          description: 'Retrait de consentement enregistré',
        });
        ToastService.success('Retrait de consentement enregistré');
        render();
      })();
    }
  });

  section.querySelector('#btn-export-consents')?.addEventListener('click', () => {
    if (!canMutate) {
      ToastService.warning('Export réservé aux profils autorisés');
      return;
    }
    const nameById = new Map(salaries.map((s) => [s.id, `${s.prenom} ${s.nom}`]));
    const header = 'id;type;sujet;date;methode;version;consenti;retrait\n';
    const rows = consents
      .map((c) => {
        const name =
          c.subject_type === 'employee' ? nameById.get(c.subject_id) ?? c.subject_id : c.subject_id;
        return [
          c.id,
          c.subject_type,
          name,
          c.consent_date,
          c.consent_method,
          c.policy_version,
          c.consent_given ? 'oui' : 'non',
          c.withdrawn_at ?? '',
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(';');
      })
      .join('\n');
    downloadCsv(header + rows, `consentements-${new Date().toISOString().slice(0, 10)}.csv`);
    ToastService.success('Export CSV téléchargé');
  });

  // Expose for salarié self-service footer link
  (window as unknown as Record<string, unknown>)['openActivePrivacyPolicy'] = () => {
    const active = activePrivacyPolicy(policies);
    if (!active) {
      ToastService.info('Aucune politique de confidentialité publiée');
      return;
    }
    openFormModal(section, { title: `Politique ${active.version}`, size: 'lg' }, (host, close) => {
      host.innerHTML = `<p style="font-size:12px;color:var(--gris)">Entrée en vigueur : ${esc(active.effective_date)}</p>
        <div style="white-space:pre-wrap;font-size:13px;line-height:1.55;margin-top:8px">${esc(active.content)}</div>
        <div style="margin-top:16px;text-align:right"><button type="button" class="btn btn-primary" data-close>Fermer</button></div>`;
      host.querySelector('[data-close]')?.addEventListener('click', close);
    });
  };
};

/** Enregistre un consentement salarié (création). */
export async function recordEmployeeConsent(
  ctx: Parameters<ViewController>[1],
  salarieId: string,
  input: {
    consent_given: boolean;
    consent_method: DataConsent['consent_method'];
    notes?: string;
  },
): Promise<void> {
  const tid = tenantIdOf(ctx);
  const policies = await loadRegister<PrivacyPolicy>(tid, 'legal.privacy_policies');
  const { ensureActivePrivacyPolicy } = await import('@domain/legal/DefaultPrivacyPolicy');
  const session = ctx.auth.getSession();
  const ensured = ensureActivePrivacyPolicy(policies, session?.user_id ?? 'system');
  if (ensured.seeded) {
    await saveRegister(tid, 'legal.privacy_policies', ensured.policies);
  }
  const active = ensured.active;
  const consents = await loadRegister<DataConsent>(tid, 'legal.data_consents');
  const row: DataConsent = {
    id: uuid('cns'),
    subject_type: 'employee',
    subject_id: salarieId,
    policy_version: active.version,
    consent_given: input.consent_given,
    consent_date: new Date().toISOString(),
    consent_method: input.consent_method,
    collected_by: session?.user_id ?? 'admin',
    notes: input.notes,
  };
  await saveRegister(tid, 'legal.data_consents', [...consents, row]);
  await recordAuditEvent(ctx, {
    action: 'create',
    entity: 'data_consent',
    entity_id: row.id,
    description: `Consentement ${row.consent_given ? 'donné' : 'refusé'} — salarié ${salarieId}`,
  });
}

/** Enregistre un consentement candidat (création / modification). */
export async function recordCandidateConsent(
  ctx: Parameters<ViewController>[1],
  candidateId: string,
  input: {
    consent_given: boolean;
    consent_method: DataConsent['consent_method'];
    notes?: string;
  },
): Promise<void> {
  const tid = tenantIdOf(ctx);
  const policies = await loadRegister<PrivacyPolicy>(tid, 'legal.privacy_policies');
  const { ensureActivePrivacyPolicy } = await import('@domain/legal/DefaultPrivacyPolicy');
  const session = ctx.auth.getSession();
  const ensured = ensureActivePrivacyPolicy(policies, session?.user_id ?? 'system');
  if (ensured.seeded) {
    await saveRegister(tid, 'legal.privacy_policies', ensured.policies);
  }
  const active = ensured.active;
  const consents = await loadRegister<DataConsent>(tid, 'legal.data_consents');
  const row: DataConsent = {
    id: uuid('cns'),
    subject_type: 'candidate',
    subject_id: candidateId,
    policy_version: active.version,
    consent_given: input.consent_given,
    consent_date: new Date().toISOString(),
    consent_method: input.consent_method,
    collected_by: session?.user_id ?? 'admin',
    notes: input.notes,
  };
  await saveRegister(tid, 'legal.data_consents', [...consents, row]);
  await recordAuditEvent(ctx, {
    action: 'create',
    entity: 'data_consent',
    entity_id: row.id,
    description: `Consentement ${row.consent_given ? 'donné' : 'refusé'} — candidat ${candidateId}`,
  });
}
